import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { itemAPI, promoAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PromoSlot from '../components/common/PromoSlot';
import { Heart, Bookmark, SlidersHorizontal, ChevronDown, ChevronUp, Star, MapPin, Truck, Sparkles, ShieldCheck, Lock } from 'lucide-react';

const PROMO_INTERVAL = 5;

const CATEGORIES = [
  'Clothes & Shoes',
  'Toys & Games',
  'Feeding',
  'Diapering',
  'Bathing & Skincare',
  'Health & Safety',
  'Nursery',
  'Strollers & Car Seats',
  'Activity & Entertainment',
  'Books',
  'Baby Gear',
  'Other',
];

const CONDITIONS = ['New with Tags', 'Like New', 'Good', 'Fair', 'Well Loved'];

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Pool of active inline promos, fetched once and shuffled. We hand them out
  // in order across the whole feed so the same ad never repeats until every
  // distinct ad has been shown (no duplicates visible at once).
  const [promoPool, setPromoPool] = useState([]);

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    condition: searchParams.get('condition') || '',
    ageGroup: searchParams.get('ageGroup') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setShowFilters(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const params = {};
    if (filters.category) params.category = filters.category;
    if (filters.condition) params.condition = filters.condition;
    if (filters.ageGroup) params.ageGroup = filters.ageGroup;
    if (filters.minPrice) params.minPrice = filters.minPrice;
    if (filters.maxPrice) params.maxPrice = filters.maxPrice;
    if (searchParams.get('search')) params.search = searchParams.get('search');

    setSearchParams(params);
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      condition: '',
      ageGroup: '',
      minPrice: '',
      maxPrice: '',
    });
    setSearchParams(searchParams.get('search') ? { search: searchParams.get('search') } : {});
  };

  const lastFetchRef = useRef(Date.now());

  const fetchItems = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = {
        search: searchParams.get('search') || '',
        category: searchParams.get('category') || '',
        condition: searchParams.get('condition') || '',
        ageGroup: searchParams.get('ageGroup') || '',
        minPrice: searchParams.get('minPrice') || '',
        maxPrice: searchParams.get('maxPrice') || '',
        page: 1,
        limit: 20,
      };

      const response = await itemAPI.getItems(params);
      setItems(response.data.data || []);
      if (typeof response.data.total === 'number') {
        setTotalItems(response.data.total);
      }
      lastFetchRef.current = Date.now();
    } catch (err) {
      setError('Failed to load items');
      console.error('Error fetching items:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Fetch the inline-promo pool once and shuffle it.
  useEffect(() => {
    let cancelled = false;
    promoAPI
      .getActiveBySlot('discover_feed_inline')
      .then(({ data }) => {
        if (cancelled) return;
        const pool = [...(data?.data || [])];
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setPromoPool(pool);
      })
      .catch(() => { if (!cancelled) setPromoPool([]); });
    return () => { cancelled = true; };
  }, []);

  // Randomized offset for inline promos — stable per load of the feed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const promoOffset = useMemo(() => Math.floor(Math.random() * PROMO_INTERVAL), [items.length]);

  // Auto-refetch when the user returns to the tab (10s debounce)
  useEffect(() => {
    const handleRefocus = () => {
      if (document.visibilityState !== 'visible') return;
      const sinceLastFetch = Date.now() - lastFetchRef.current;
      if (sinceLastFetch > 10000) {
        fetchItems({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleRefocus);
    window.addEventListener('focus', handleRefocus);
    return () => {
      document.removeEventListener('visibilitychange', handleRefocus);
      window.removeEventListener('focus', handleRefocus);
    };
  }, [fetchItems]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{
      background: 'var(--background)',
      minHeight: '100vh',
      paddingTop: '24px',
      paddingBottom: '40px',
    }}>
      <div className="container" style={{ maxWidth: '1400px' }}>
        {!isAuthenticated && !searchParams.get('search') && (
          <div
            style={{
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              padding: isMobile ? '24px 20px' : '40px 48px',
              marginBottom: '28px',
            }}
          >
            <h1
              style={{
                fontSize: isMobile ? '26px' : '36px',
                fontWeight: 700,
                color: 'var(--text)',
                lineHeight: 1.2,
                marginBottom: '10px',
              }}
            >
              Baby gear resale, by parents, for parents.
            </h1>
            <p
              style={{
                fontSize: isMobile ? '15px' : '17px',
                color: 'var(--textSecondary)',
                lineHeight: 1.5,
                marginBottom: '20px',
                maxWidth: '640px',
              }}
            >
              Built by a dad — because we've lived it. Buy and sell gently-used baby and kids' gear with secure payments and buyer protection built in.
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <Link
                to="/register"
                className="btn btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Start selling
              </Link>
              <a
                href="#feed"
                className="btn btn-outline"
                style={{ textDecoration: 'none' }}
              >
                Browse items
              </a>
            </div>

            <div
              style={{
                display: 'flex',
                gap: isMobile ? '16px' : '24px',
                flexWrap: 'wrap',
                fontSize: '13px',
                color: 'var(--textSecondary)',
                fontWeight: 500,
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Lock size={14} color="#0a7a5b" />
                Secure payments via Stripe
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <ShieldCheck size={14} color="#0a7a5b" />
                Escrow buyer protection
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={14} color="#f59e0b" />
                Verified sellers
              </span>
            </div>
          </div>
        )}

        <h1 id="feed" style={{ margin: '0 0 20px 0', fontSize: isMobile ? '24px' : '28px', scrollMarginTop: '80px' }}>
          {searchParams.get('search') ? `Search results for "${searchParams.get('search')}"` : 'Discover Baby Items'}
        </h1>

        {/* Category banner promo slot */}
        {filters.category && <PromoSlot slotId="category_banner" variant="banner" />}

        {error && <div className="error-message">{error}</div>}

        {/* Compact Filter Bar */}
        <div style={{ marginBottom: '24px' }}>
          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '10px 18px',
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text)',
              marginBottom: showFilters ? '12px' : '0',
            }}
          >
            <SlidersHorizontal size={16} />
            Filters
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Collapsible Filter Panel */}
          {showFilters && (
            <div
              style={{
                background: 'var(--cardBackground)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '20px',
              }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '16px',
              }}>
                {/* Category Filter */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'var(--background)',
                    }}
                  >
                    <option value="">All Categories</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Condition Filter */}
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                    Condition
                  </label>
                  <select
                    value={filters.condition}
                    onChange={(e) => handleFilterChange('condition', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'var(--background)',
                    }}
                  >
                    <option value="">All Conditions</option>
                    {CONDITIONS.map((cond) => (
                      <option key={cond} value={cond}>{cond}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                    Price Range
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'var(--background)',
                      }}
                    />
                    <span style={{ color: 'var(--textSecondary)' }}>—</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: 'var(--background)',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--cardBackground)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Clear All
                </button>
                <button
                  onClick={applyFilters}
                  style={{
                    padding: '8px 20px',
                    background: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Growing-marketplace banner — only when inventory is small and no filters */}
        {totalItems !== null && totalItems < 3 && !searchParams.toString() && items.length > 0 && (
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '10px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              color: 'var(--text)',
            }}
          >
            <Sparkles size={16} color="#0a7a5b" />
            <div>
              <strong>New marketplace, growing daily.</strong>{' '}
              <span style={{ color: 'var(--textSecondary)' }}>
                We're just getting started — be one of the first sellers and your listing stays on top.{' '}
                <Link
                  to="/register"
                  style={{ color: '#0a7a5b', fontWeight: 700, textDecoration: 'underline' }}
                >
                  List an item
                </Link>
              </span>
            </div>
          </div>
        )}

        {/* Items Grid */}
        {items.length === 0 ? (
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
        <div className="text-center" style={{ padding: '60px 20px' }}>
          {searchParams.get('search') ? (
            <>
              <h2 style={{ marginBottom: '12px' }}>No matches for "{searchParams.get('search')}"</h2>
              <p className="text-muted" style={{ marginBottom: '20px' }}>
                Try a broader search, or clear filters and browse everything new parents are listing.
              </p>
              <button
                onClick={() => setSearchParams({})}
                className="btn btn-outline"
                style={{ marginRight: '8px' }}
              >
                Clear search
              </button>
            </>
          ) : filters.category ? (
            <>
              <h2 style={{ marginBottom: '12px' }}>No {filters.category} listings yet</h2>
              <p className="text-muted" style={{ marginBottom: '20px' }}>
                Be the first to list in this category — early sellers get more visibility.
              </p>
              <Link to="/create-item" className="btn btn-primary" style={{ textDecoration: 'none', marginRight: '8px' }}>
                List your {filters.category.toLowerCase()}
              </Link>
              <button onClick={clearFilters} className="btn btn-outline">
                Browse all items
              </button>
            </>
          ) : filters.condition || filters.ageGroup || filters.minPrice || filters.maxPrice ? (
            <>
              <h2 style={{ marginBottom: '12px' }}>Nothing matches these filters</h2>
              <p className="text-muted" style={{ marginBottom: '20px' }}>
                Try loosening your filters to see more listings from our growing community.
              </p>
              <button onClick={clearFilters} className="btn btn-outline">
                Clear filters
              </button>
            </>
          ) : (
            <>
              <h2 style={{ marginBottom: '12px' }}>The marketplace is just getting started</h2>
              <p className="text-muted" style={{ marginBottom: '20px' }}>
                No public listings yet — be the first. Your item goes straight to the top of the feed.
              </p>
              <Link to="/create-item" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                List the first item
              </Link>
            </>
          )}
        </div>
        {searchParams.get('search') && (
          <PromoSlot slotId="empty_search" variant="inline" />
        )}
        </div>
      ) : (
        <div
          style={{
            columnCount: window.innerWidth > 1200 ? 5 : window.innerWidth > 900 ? 4 : window.innerWidth > 600 ? 3 : 2,
            columnGap: '16px',
            padding: '0',
          }}
        >
          {(() => {
            // Page-level cursor: hand out promos in shuffled order across the
            // whole feed so the same ad never repeats until all distinct ads
            // have been shown, and a pair never contains the same ad twice.
            let promoCursor = 0;
            return items.map((item, index) => {
              const promoHere = index > 0 && (index - promoOffset) % PROMO_INTERVAL === 0;
              let promoCards = null;
              if (promoHere && promoPool.length > 0) {
                const n = Math.min(2, promoPool.length);
                promoCards = [];
                for (let k = 0; k < n; k++) {
                  const p = promoPool[promoCursor % promoPool.length];
                  promoCursor++;
                  promoCards.push(
                    <div key={`promo-${index}-${p._id}-${k}`} style={{ breakInside: 'avoid', marginBottom: '16px' }}>
                      <PromoSlot slotId="discover_feed_inline" placement={p} variant="card" />
                    </div>
                  );
                }
              }
              return (
                <React.Fragment key={item._id}>
                  {promoCards}
                  <div style={{ breakInside: 'avoid', marginBottom: '16px' }}>
                    <ItemCard item={item} />
                  </div>
                </React.Fragment>
              );
            });
          })()}
        </div>
      )}
      </div>
    </div>
  );
};

const ItemCard = ({ item }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // Initialize like/save state
  useEffect(() => {
    if (isAuthenticated && user) {
      setLiked(item.likes?.includes(user._id) || false);
      setSaved(user.savedItems?.includes(item._id) || false);
    }
    setLikeCount(item.likes?.length || 0);
  }, [item, user, isAuthenticated]);

  // Handle like toggle
  const handleLikeToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (liked) {
        await itemAPI.unlikeItem(item._id);
        setLiked(false);
        setLikeCount((prev) => prev - 1);
      } else {
        await itemAPI.likeItem(item._id);
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  // Handle save toggle
  const handleSaveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (saved) {
        await itemAPI.unsaveItem(item._id);
        setSaved(false);
      } else {
        await itemAPI.saveItem(item._id);
        setSaved(true);
      }
    } catch (err) {
      console.error('Failed to toggle save:', err);
    }
  };

  // Helper to get image URL from various formats
  const getImageUrl = (item) => {
    // Handle images array (objects with fullSize/thumbnail)
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      const primaryImg = item.images.find(img => img.isPrimary) || item.images[0];

      if (typeof primaryImg === 'object') {
        return primaryImg.thumbnail || primaryImg.fullSize || primaryImg.url;
      }
      if (typeof primaryImg === 'string') {
        return primaryImg;
      }
    }

    // Handle direct thumbnail/image properties
    if (item.thumbnail) return item.thumbnail;
    if (item.image) return item.image;

    // Fallback placeholder
    return `https://via.placeholder.com/400x400?text=${encodeURIComponent(item.title || 'No Image')}`;
  };

  const imageUrl = getImageUrl(item);

  return (
    <Link
      to={`/items/${item._id}`}
      className="card"
      style={{
        padding: 0,
        overflow: 'hidden',
        display: 'block',
        borderRadius: '16px',
        backgroundColor: 'var(--cardBackground)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ position: 'relative', backgroundColor: '#f5f5f5' }}>
        <img
          src={imageUrl}
          alt={item.title}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x400?text=Image+Error';
          }}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '16px 16px 0 0',
          }}
        />

        {/* Like button with count */}
        <button
          onClick={handleLikeToggle}
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: 'rgba(250, 248, 245, 0.95)',
            border: 'none',
            borderRadius: '20px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm)',
            zIndex: 10,
          }}
          title="Like this item"
        >
          <Heart
            size={18}
            fill={liked ? '#e60023' : 'none'}
            color={liked ? '#e60023' : '#666'}
          />
          {likeCount > 0 && (
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
              {likeCount}
            </span>
          )}
        </button>

        {/* Save/Bookmark button */}
        <button
          onClick={handleSaveToggle}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(250, 248, 245, 0.95)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm)',
            zIndex: 10,
          }}
          title="Save for later"
        >
          <Bookmark
            size={18}
            fill={saved ? '#2196F3' : 'none'}
            color={saved ? '#2196F3' : '#666'}
          />
        </button>

        {item.condition && (
          <span
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              background: 'rgba(250, 248, 245, 0.95)',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            {item.condition}
          </span>
        )}
      </div>

      <div style={{ padding: '12px' }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--text)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
            marginBottom: '8px',
          }}
        >
          {item.title}
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--primary)' }}>
            ${item.price}
          </div>
          {(item.size || item.ageGroup) && (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '999px',
                backgroundColor: 'var(--secondary)',
                color: 'var(--textSecondary)',
                fontSize: '11px',
                fontWeight: 500,
              }}
            >
              {item.size ? `Size ${item.size}` : item.ageGroup}
            </span>
          )}
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          paddingTop: '8px',
          borderTop: '1px solid var(--border)',
          fontSize: '12px',
          color: 'var(--textSecondary)',
        }}>
          {item.user && typeof item.user === 'object' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                {item.user.username || 'Seller'}
              </span>
              {item.user.totalRatings > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Star size={11} fill="#f59e0b" color="#f59e0b" />
                  <span style={{ fontWeight: 500 }}>{(item.user.averageRating || 0).toFixed(1)}</span>
                  <span style={{ opacity: 0.6 }}>({item.user.totalRatings})</span>
                </span>
              ) : (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(16,185,129,0.1)',
                    color: '#0a7a5b',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                >
                  <Sparkles size={9} />
                  New
                </span>
              )}
            </div>
          )}

          {(item.location?.city || item.shippingOptions?.shipping) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {item.location?.city && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <MapPin size={11} />
                  {item.location.city}{item.location.state ? `, ${item.location.state}` : ''}
                </span>
              )}
              {item.shippingOptions?.shipping && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Truck size={11} />
                  Ships
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default Home;
